import {
  publicCatalogService,
  type Product as ApiProduct,
  type Category as ApiCategory,
} from '@kss/api';
import { toAbsoluteImageUrl } from '../utils/imageUrl';
import {
  type ShopAttribute,
  type ShopProduct,
  type ShopSection,
  type ShopVariant,
} from './data';

/**
 * Build a URL-safe slug from a free-text title. Lowercase, ascii-only,
 * dashes between words, max 80 chars. Mirrors the AI assistant's
 * "ashwagandha-whole-roots" output format.
 */
function slugify(s: string): string {
  return (
    (s ?? '')
      .toString()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'product'
  );
}

/**
 * Try to parse the optional `sectionsJson` column on the live Product
 * row. Backend agent has been asked to add this column (see
 * `BACKEND_AGENT_PROMPTS.md`); until it ships, the field is absent
 * and we fall back to deriving a default 3-section block.
 */
function parseSectionsJson(raw: unknown): ShopSection[] | null {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const valid: ShopSection[] = [];
    for (const s of parsed) {
      if (!s || typeof s !== 'object') continue;
      const name = String((s as { name?: unknown }).name ?? '');
      const type = String((s as { type?: unknown }).type ?? '');
      if (type === 'text') {
        valid.push({
          name,
          type: 'text',
          content: String((s as { content?: unknown }).content ?? ''),
          features: Array.isArray((s as { features?: unknown }).features)
            ? ((s as { features: unknown[] }).features.map(String))
            : undefined,
        });
      } else if (type === 'keyvalue') {
        const attrs = Array.isArray((s as { attributes?: unknown }).attributes)
          ? ((s as { attributes: unknown[] }).attributes
              .filter((a): a is { name: string; value: string } => !!a && typeof a === 'object')
              .map((a) => ({ name: String((a as { name?: unknown }).name ?? ''), value: String((a as { value?: unknown }).value ?? '') })))
          : [];
        valid.push({ name, type: 'keyvalue', attributes: attrs });
      }
    }
    return valid;
  } catch {
    return null;
  }
}

function defaultSections(args: {
  description: string;
  specs: ShopAttribute[];
  brand?: string;
}): ShopSection[] {
  const sections: ShopSection[] = [];
  if (args.description || args.specs.length > 0) {
    // Description block — pulls bullet "features" out of the
    // description if it contains hyphen / bullet lines, else just
    // shows the paragraph.
    const features = args.description
      .split(/\r?\n+/)
      .map((line) => line.replace(/^[-•·*]\s*/, '').trim())
      .filter((line) => line && line.length < 100 && line !== args.description);
    sections.push({
      name: 'Description',
      type: 'text',
      content: args.description,
      features: features.length >= 2 ? features.slice(0, 6) : undefined,
    });
  }
  if (args.specs.length > 0) {
    sections.push({
      name: 'Specifications',
      type: 'keyvalue',
      attributes: args.specs,
    });
  }
  /* No Shipping section.

     There used to be a hardcoded one here advertising "Free over ₹999",
     "Express Shipping · ₹99" and "30-day returns". None of it was
     configurable, none of it was true, and the ₹999 threshold directly
     contradicted the cart's own hardcoded "free over ₹5,000" — a
     shopper was quoted one rule on the product page and charged
     another two clicks later. "30-day returns" was worse: a binding
     commercial commitment printed on every product of every tenant,
     none of whom had agreed to it.

     Delivery terms now live on the Store (`shippingCharge` /
     `freeShippingThreshold`) and are quoted where they can be quoted
     accurately — in the cart and at checkout, against a real subtotal.
     A per-product shipping section belongs here only once products
     carry their own shipping data; until then, silence beats fiction. */
  return sections;
}

/**
 * Adapter layer between the live `/api/Product/all` payload and the
 * `ShopProduct` shape that ProductCard / ProductDetail expect.
 *
 * The live API ships the canonical fields (`name`, `sellingPrice`,
 * `mrpPrice`, `imageUrl`, `description`, `brand`, `categoryId`) but the
 * storefront expects display-friendly aliases (`title`, `price`,
 * `originalPrice`, `image`, `category` slug). This module bridges the
 * two without leaking ids into the public-web component tree.
 *
 * Why a dedicated adapter (vs. fixing the types upstream)? `@kss/ui`
 * `Product` is a small DTO scoped to the storefront card layout —
 * keeping the domain (ApiProduct) and the view (Product) separate
 * means panel and shop can evolve independently.
 */

export interface ShopCategory {
  id: string;
  label: string;
  count?: number;
}

const FALLBACK_GRADIENTS = [
  'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
  'linear-gradient(135deg, #ec4899 0%, #ef4444 100%)',
  'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
];

/**
 * Stable hash → palette index so the same product keeps the same
 * background gradient across reloads when imageUrl is missing.
 */
function hashIndex(s: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % mod;
}

export function adaptProduct(
  p: ApiProduct,
  categoriesById: Record<string, string>,
): ShopProduct {
  const id = String(p.productId ?? '');
  // Title fallback chain: name → sku → "Untitled". Some panel-created
  // products in the wild ship with `name: ""` (form left blank) or
  // `name: null`, which used to render as a totally blank tile. Using
  // the SKU keeps the card identifiable while the merchant fixes the
  // row.
  const rawName = (p.name ?? '').toString().trim();
  const rawSku = (p.sku ?? '').toString().trim();
  const title = rawName || rawSku || 'Untitled';
  const price = Number(p.sellingPrice ?? 0);
  const mrp = Number(p.mrpPrice ?? 0);
  const categoryLabel = (p.categoryId && categoriesById[p.categoryId]) || '';

  // Derive specs from a few canonical product fields so the PDP
  // tabs have something to render — the live API doesn't yet expose
  // a structured specs[] array.
  //
  // Order matters: shoppers expect the most identifying / commercial
  // fields first (Brand, Category, SKU), measurements next (Weight,
  // Dimensions), and tax / barcode metadata last.
  const specs: { label: string; value: string }[] = [];
  if (p.brand) specs.push({ label: 'Brand', value: String(p.brand) });
  if (categoryLabel) specs.push({ label: 'Category', value: categoryLabel });
  if (p.sku) specs.push({ label: 'SKU', value: String(p.sku) });
  if (p.weight != null) specs.push({ label: 'Weight', value: `${p.weight} kg` });
  if (p.unit) specs.push({ label: 'Unit', value: String(p.unit) });
  // Dimensions live on a future column (`Product.dimensions`) — see
  // BACKEND_AGENT_PROMPTS Item G. Until it ships, the field is
  // skipped silently. Once present it will surface here automatically.
  if ((p as { dimensions?: string }).dimensions) {
    specs.push({ label: 'Dimensions', value: String((p as { dimensions?: string }).dimensions) });
  }
  if (p.hsnCode) specs.push({ label: 'HSN', value: String(p.hsnCode) });
  if (p.barcode) specs.push({ label: 'Barcode', value: String(p.barcode) });
  if (p.taxRate != null) specs.push({ label: 'Tax', value: `${p.taxRate}%` });

  // Emit both the legacy `specs` shape (kept on the type for any
  // residual consumer) and the new `sections` array (the canonical
  // structured payload rendered on the detail page).
  const legacySpecs = specs.map((s) => ({ label: s.label, value: s.value }));
  const sectionAttributes: ShopAttribute[] = specs.map((s) => ({ name: s.label, value: s.value }));

  // Prefer sections shipped by the backend (`sectionsJson` column —
  // see BACKEND_AGENT_PROMPTS.md). Fall back to a derived default
  // built from description + specs so every product still renders
  // a clean accordion before the AI-generated payload exists.
  const sectionsFromApi = parseSectionsJson((p as { sectionsJson?: unknown }).sectionsJson);
  const sections =
    sectionsFromApi && sectionsFromApi.length > 0
      ? sectionsFromApi
      : defaultSections({
          description: p.description ?? '',
          specs: sectionAttributes,
          brand: p.brand,
        });

  // Variant axes — derive from the live `productAttributes[]` array
  // (a list of {attributeId, attributeValueId} joins). Without an
  // attribute name lookup here we only expose distinct attribute
  // ids; the panel admin form is the canonical place to set them.
  const apiVariants = (p.variants ?? []) as { name?: string; sku?: string }[];
  const variants: ShopVariant[] = apiVariants.length > 0
    ? [{ name: 'Variant', options: apiVariants.map((v) => v.name ?? v.sku ?? '').filter(Boolean) }]
    : [];

  return {
    id,
    title,
    price,
    originalPrice: mrp > price ? mrp : undefined,
    // Convert tenant-relative `/uploads/...` paths to an absolute
    // API-host URL so the storefront (which is served from a
    // different origin in prod) loads them correctly.
    image: toAbsoluteImageUrl(p.imageUrl) || undefined,
    // Gallery — same absolutization as the hero. Empty / falsy urls
    // are dropped; if nothing survives the field is left undefined
    // so downstream consumers can treat "no gallery" as absent.
    images: Array.isArray(p.images)
      ? (() => {
          const abs = p.images!
            .map((u) => toAbsoluteImageUrl(u))
            .filter((u): u is string => !!u);
          return abs.length > 0 ? abs : undefined;
        })()
      : undefined,
    imageBg: p.imageUrl ? undefined : FALLBACK_GRADIENTS[hashIndex(id, FALLBACK_GRADIENTS.length)],
    category: categoryLabel || undefined,
    /* Rating / review count DO ship on the live card DTO
       (`PublicStorefrontService.MapCard` projects `Rating` and
       `ReviewsCount`). They were hardcoded to `undefined` here on the
       assumption the API lacked them, which left the storefront's
       "Rating" and "Best sellers" sorts comparing 0 against 0 — both
       were inert no-ops. `undefined` is preserved for a genuinely
       unrated product so the card hides the stars rather than
       advertising a 0-star rating. */
    rating: Number(p.rating) > 0 ? Number(p.rating) : undefined,
    reviews: Number(p.reviewsCount) > 0 ? Number(p.reviewsCount) : undefined,
    /* Creation timestamp, used by the "New arrivals" sort. Also on the
       card DTO — tagged `F5 — storefront New Arrivals sort` server-side. */
    createdAt: p.createdAt,
    /* GST rate for this product, straight off the card DTO.

       Previously dropped here, which is why checkout invoiced a flat
       18% for everything: the PDP rendered the real rate as a "Tax: 5%"
       spec (see the `specs` block above) while the invoice charged 18%
       on the same line. Carried through so `CheckoutPage` can price each
       line at its own rate. `undefined` when unset — the caller decides
       the fallback rather than this layer inventing a rate. */
    taxRate: p.taxRate != null ? Number(p.taxRate) : undefined,
    // The storefront never shows an "Inactive" badge — inactive rows
    // are filtered out one level up (in `loadShopCatalog`) so they
    // simply don't render. Anything that survives is purchasable.
    badge: undefined,
    description: p.description ?? '',
    brand: p.brand ?? '',
    inStock: p.isActive !== false,
    specs: legacySpecs,
    slug: slugify(title),
    sections,
    variants: variants.length > 0 ? variants : undefined,
  };
}

/**
 * Source tag returned alongside the catalog so the UI can react:
 *
 * - `live`  — at least one product came from `/api/Public/products`.
 * - `empty` — backend was reachable but returned zero products
 *             (either the tenant has no products, or a hard network
 *             error swallowed both calls). Storefront renders the
 *             "no products" empty state — never mock data.
 *
 * The previous `'mock'` source has been removed: the storefront now
 * shows ONLY live tenant data.
 */
export type ShopCatalogSource = 'live' | 'empty';

/**
 * Load the storefront catalog: products + categories, mapped to the
 * shop's view types in one round-trip.
 *
 * Reads the **anonymous** `/api/Public/{products,categories}`
 * endpoints — the panel and the storefront see the same rows for
 * a given tenant without forcing the shopper to sign in first. The
 * tenant code is resolved per-request by `configurePublicCatalog()`
 * in `apps/web/src/main.tsx` (hostname → localStorage → default).
 *
 * Network errors silently degrade to the same empty state as a
 * truly-empty tenant — there is no mock fallback any more.
 */
export async function loadShopCatalog(): Promise<{
  products: ShopProduct[];
  categories: ShopCategory[];
  source: ShopCatalogSource;
}> {
  const [apiProducts, apiCategories] = await Promise.all([
    /* Full set: this builds the category facet counts and the brand
       list, both of which are counts over the WHOLE catalog rather than
       over one page. Once ShopList moves to server-side querying
       (Phase 2) these facets are what should move with it. */
    publicCatalogService.allProducts().catch(() => [] as ApiProduct[]),
    publicCatalogService.categories().catch(() => [] as ApiCategory[]),
  ]);

  // Backend empty (or unreachable) — render an empty storefront
  // state. We deliberately do NOT fall back to demo / seed products.
  if (!apiProducts || apiProducts.length === 0) {
    return { products: [], categories: [{ id: 'all', label: 'All', count: 0 }], source: 'empty' };
  }

  // categoryId → category name lookup so adaptProduct can stamp a
  // human-readable category label on each card.
  const byId: Record<string, string> = {};
  for (const c of apiCategories ?? []) {
    if (c.categoryId && c.name) byId[c.categoryId] = c.name;
  }

  // Storefront only ever shows live, sellable rows. The backend's
  // `/api/Public/products` should already filter, but we double-check
  // here so an admin's freshly-deactivated product disappears from
  // the storefront on the next reload even before the cache clears.
  const products = apiProducts
    .filter((p) => p.isActive !== false)
    .map((p) => adaptProduct(p, byId));

  // Per-category product counts so the sidebar can show "(12)" next to
  // each category without a second roundtrip.
  const counts = new Map<string, number>();
  for (const p of products) {
    if (p.category) counts.set(p.category, (counts.get(p.category) ?? 0) + 1);
  }

  const categories: ShopCategory[] = [
    { id: 'all', label: 'All', count: products.length },
    ...(apiCategories ?? [])
      .filter((c) => c.isActive !== false && c.categoryId && c.name)
      .map((c) => ({
        id: c.name!,
        label: c.name!,
        count: counts.get(c.name!) ?? 0,
      })),
  ];

  return { products, categories, source: 'live' };
}
